import type { BallotSelection, PollResult, PollResultTeam } from './types.js';
import { BALLOT_SIZE, POLL_SIZE } from './scoring.js';

/**
 * Rank assigned to a team that the official poll left outside the Top 25.
 * Sits just outside the ballot so "I ranked a team nobody else did" costs a
 * predictable, bounded amount rather than an unbounded one.
 */
export const UNRANKED_SENTINEL = 30;

/**
 * Worst possible footrule distance: every one of the voter's 25 picks missed
 * the poll entirely. sum(30 - i) for i in 1..25 = 425.
 */
export const MAX_FOOTRULE_DISTANCE = Array.from(
  { length: BALLOT_SIZE },
  (_, index) => UNRANKED_SENTINEL - (index + 1),
).reduce((sum, value) => sum + value, 0);

export interface ConsensusComparisonRow {
  teamId: string;
  voterRank: number;
  pollRank: number | null;
  /** Positive = the voter was higher on this team than the poll was. */
  difference: number | null;
}

export interface ConsensusResult {
  /** 0-100. 100 means the ballot matched the released poll exactly. */
  score: number;
  distance: number;
  rows: ConsensusComparisonRow[];
  exactMatches: number;
  /** Teams the voter ranked that the poll left out of the Top 25. */
  outliers: ConsensusComparisonRow[];
}

function pollRankMap(poll: Pick<PollResult, 'teams'>): Map<string, number> {
  const map = new Map<string, number>();
  for (const team of poll.teams) {
    if (team.rank <= POLL_SIZE) map.set(team.teamId, team.rank);
  }
  return map;
}

/**
 * Consensus Score - normalized Spearman footrule distance between a ballot and
 * the released poll, expressed as a similarity percentage.
 *
 * This is purely a fun/engagement metric. It never affects ballot weight:
 * every valid voter counts exactly the same in calculatePoll().
 */
export function compareBallotToPoll(
  selections: BallotSelection[],
  poll: Pick<PollResult, 'teams'> | null | undefined,
): ConsensusResult {
  if (!poll || selections.length === 0) {
    return { score: 0, distance: 0, rows: [], exactMatches: 0, outliers: [] };
  }

  const ranks = pollRankMap(poll);
  const rows: ConsensusComparisonRow[] = [];
  let distance = 0;
  let exactMatches = 0;

  for (const selection of selections) {
    const pollRank = ranks.get(selection.teamId) ?? null;
    const effectivePollRank = pollRank ?? UNRANKED_SENTINEL;
    distance += Math.abs(selection.rank - effectivePollRank);
    if (pollRank === selection.rank) exactMatches += 1;
    rows.push({
      teamId: selection.teamId,
      voterRank: selection.rank,
      pollRank,
      difference: pollRank === null ? null : pollRank - selection.rank,
    });
  }

  // Scale by how many slots were actually used so partial ballots are judged
  // on the same curve as complete ones.
  const maxDistance = (MAX_FOOTRULE_DISTANCE * selections.length) / BALLOT_SIZE;
  const ratio = maxDistance === 0 ? 1 : distance / maxDistance;
  const score = Math.max(0, Math.min(100, Math.round((1 - ratio) * 100)));

  rows.sort((a, b) => a.voterRank - b.voterRank);

  return {
    score,
    distance,
    rows,
    exactMatches,
    outliers: rows.filter((row) => row.pollRank === null),
  };
}

/** Convenience wrapper used wherever only the headline percentage is needed. */
export function consensusScore(
  selections: BallotSelection[],
  poll: Pick<PollResult, 'teams'> | null | undefined,
): number | null {
  if (!poll) return null;
  return compareBallotToPoll(selections, poll).score;
}

/** Averages a voter's weekly consensus scores into a season figure. */
export function averageConsensus(scores: (number | null | undefined)[]): number | null {
  const valid = scores.filter((score): score is number => typeof score === 'number');
  if (valid.length === 0) return null;
  return Math.round(valid.reduce((sum, score) => sum + score, 0) / valid.length);
}

/**
 * Where a team sat on one specific ballot versus where the poll put it.
 * Powers the "John: #1 / FanVote25: #2 / +1" rows on public ballots.
 */
export function ballotDeltaLabel(difference: number | null): string {
  if (difference === null) return 'Unranked in poll';
  if (difference === 0) return 'Even';
  return difference > 0 ? `+${difference}` : `${difference}`;
}

/** Ballot heat map: how many voters placed a team at each rank. */
export function distributionBars(
  team: Pick<PollResultTeam, 'distribution' | 'ballotAppearances'>,
): { rank: number; count: number; share: number }[] {
  const bars: { rank: number; count: number; share: number }[] = [];
  const max = Math.max(1, ...Object.values(team.distribution));
  for (let rank = 1; rank <= BALLOT_SIZE; rank += 1) {
    const count = team.distribution[rank] ?? 0;
    bars.push({ rank, count, share: count / max });
  }
  return bars;
}
